import type { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { useState } from 'react';
import {
  faCalendarDay,
  faCheckCircle,
  faEllipsis,
  faQuestion,
  faTimer,
  faXmarkCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Stack, Text } from '@sixui/ui';
import { DateTime } from 'luxon';
import { useInterval } from 'usehooks-ts';

import type { IContainerProps } from '~/components/utils/Container';
import type {
  IApiBuildStepStatusKey,
  IApiPipelineResponseBody,
} from '~/lib/api/modules/Builds';
import { Duration } from '~/components/atoms/Duration';
import { RelativeTime } from '~/components/atoms/RelativeTime';
import { ITimeUnit } from '~/helpers/types';

export type IPipelineStatusProps = IContainerProps & {
  pipeline: IApiPipelineResponseBody;
};

const getIconFromStatus = (status: IApiBuildStepStatusKey): IconDefinition => {
  switch (status) {
    case 'running':
      return faEllipsis;
    case 'error':
      return faXmarkCircle;
    case 'success':
      return faCheckCircle;
    default:
      return faQuestion;
  }
};

export const PipelineStatus: React.FC<IPipelineStatusProps> = (props) => {
  const { sx, pipeline } = props;
  const [durationInMs, setDurationInMs] = useState(
    pipeline.current.endedAt
      ? DateTime.fromISO(pipeline.current.endedAt)
          .diff(DateTime.fromISO(pipeline.createdAt))
          .as('milliseconds')
      : 0,
  );
  const currentStepIndex =
    pipeline.pipeline.findIndex((step) => step.step === pipeline.current.step) +
    1;
  useInterval(
    () =>
      setDurationInMs(
        pipeline.current.endedAt
          ? DateTime.fromISO(pipeline.current.endedAt)
              .diff(DateTime.fromISO(pipeline.createdAt))
              .as('milliseconds')
          : DateTime.now()
              .diff(DateTime.fromISO(pipeline.createdAt))
              .as('milliseconds'),
      ),
    pipeline.current.status === 'running' ? 1 * ITimeUnit.SECOND : null,
  );

  return (
    <Stack sx={sx} gap={1}>
      <Stack horizontal gap={2}>
        <FontAwesomeIcon
          icon={getIconFromStatus(pipeline.current.status)}
          fixedWidth
        />
        <Text truncate>
          {pipeline.current.status === 'running'
            ? `Step ${currentStepIndex}/${pipeline.pipeline.length}`
            : pipeline.current.status === 'success'
              ? 'Passed'
              : 'Failed'}
        </Text>
      </Stack>
      <Stack horizontal gap={2}>
        <FontAwesomeIcon icon={faTimer} fixedWidth />
        <Text as="div" truncate>
          <Duration ms={durationInMs} />
        </Text>
      </Stack>
      <Stack horizontal gap={2}>
        <FontAwesomeIcon icon={faCalendarDay} fixedWidth />
        <Text as="div" truncate>
          <RelativeTime isoDate={pipeline.createdAt} />
        </Text>
      </Stack>
    </Stack>
  );
};
